import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// ✅ Import section background only
import projectBg from "../../images/bg/project-bg.png";

// ── TrustEngine Product Screen SVGs (one per case study) ───────────────────
const T = "#00c896"; // teal
const N = "#0f2040"; // navy
const F = "system-ui,sans-serif";

// Shared chrome: topbar + sidebar
const Chrome: React.FC<{ activeNav: number }> = ({ activeNav }) => {
  const navItems = ["Home","Face","Watch","Doc","Legal","Credit"];
  const navY = [60,108,156,204,252,300];
  return (
    <>
      {/* Top bar */}
      <rect width="860" height="42" fill="white"/>
      <line x1="0" y1="42" x2="860" y2="42" stroke="#e5e7eb" strokeWidth="1"/>
      <path d="M18 7 L10 11V16C10 20 12.8 23.8 18 25.2 23.2 23.8 26 20 26 16V11Z" fill={T} opacity="0.15"/>
      <path d="M18 7 L10 11V16C10 20 12.8 23.8 18 25.2 23.2 23.8 26 20 26 16V11Z" stroke={T} strokeWidth="1.4" fill="none"/>
      <path d="M15 17l2.5 2.5 4-4" stroke={T} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <text x="32" y="26" fontSize="13" fontWeight="700" fill={N} fontFamily={F}>TrustEngine</text>
      <circle cx="820" cy="21" r="13" fill="#e0f7f1"/>
      <text x="820" y="26" fontSize="10" fontWeight="700" fill={T} textAnchor="middle" fontFamily={F}>JD</text>
      <text x="838" y="19" fontSize="10" fontWeight="600" fill={N} fontFamily={F}>Jane Doe</text>
      <text x="838" y="31" fontSize="9" fill="#9ca3af" fontFamily={F}>Admin</text>
      {/* Sidebar */}
      <rect x="0" y="42" width="52" height="458" fill={N}/>
      <rect x="0" y={navY[activeNav]} width="3" height="36" rx="1.5" fill={T}/>
      {navItems.map((lbl, i) => (
        <g key={i}>
          <rect x="6" y={navY[i]+2} width="40" height="28" rx="7"
            fill={i===activeNav ? "rgba(0,200,150,0.15)" : "transparent"}/>
          <text x="26" y={navY[i]+20} fontSize="8" textAnchor="middle" fontFamily={F}
            fill={i===activeNav ? T : "rgba(255,255,255,0.35)"} fontWeight={i===activeNav?"700":"400"}>{lbl}</text>
        </g>
      ))}
    </>
  );
};

// ── Variant 0: eKYC / Face Engine ──
const ScreenEKYC: React.FC = () => (
  <svg viewBox="0 0 860 500" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{width:"100%",height:"100%",display:"block"}} preserveAspectRatio="xMidYMid slice">
    <rect width="860" height="500" fill="#f4f6fb"/>
    <Chrome activeNav={1}/>
    {/* Breadcrumb */}
    <text x="66" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>Face Engine</text>
    <text x="138" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>/</text>
    <text x="148" y="62" fontSize="11" fontWeight="600" fill={N} fontFamily={F}>Verify Identity</text>
    {/* Left panel — scan UI */}
    <rect x="62" y="72" width="370" height="400" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
    <text x="82" y="100" fontSize="13" fontWeight="700" fill={N} fontFamily={F}>Live Capture</text>
    {/* Face scan circle */}
    <circle cx="247" cy="230" r="110" fill="#f0fdf9"/>
    <circle cx="247" cy="230" r="95" stroke={T} strokeWidth="2" strokeDasharray="8 4"/>
    <circle cx="247" cy="230" r="80" stroke={T} strokeWidth="1" opacity="0.4"/>
    {/* Face outline */}
    <ellipse cx="247" cy="220" rx="44" ry="52" stroke="#d1d5db" strokeWidth="1.5" fill="#fafafa"/>
    {/* Eyes */}
    <circle cx="234" cy="210" r="5" fill="#e5e7eb"/><circle cx="234" cy="210" r="2.5" fill="#374151"/>
    <circle cx="260" cy="210" r="5" fill="#e5e7eb"/><circle cx="260" cy="210" r="2.5" fill="#374151"/>
    {/* Mouth */}
    <path d="M238 232 q9 7 18 0" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    {/* Scan line */}
    <line x1="167" y1="215" x2="327" y2="215" stroke={T} strokeWidth="1.5" opacity="0.7" strokeDasharray="3 2"/>
    {/* Corner brackets */}
    {([[152,115],[322,115],[152,345],[322,345]] as [number,number][]).map(([cx,cy],i) => (
      <g key={i}>
        <line x1={cx} y1={cy} x2={cx+(i%2===0?12:-12)} y2={cy} stroke={T} strokeWidth="2" strokeLinecap="round"/>
        <line x1={cx} y1={cy} x2={cx} y2={cy+(i<2?12:-12)} stroke={T} strokeWidth="2" strokeLinecap="round"/>
      </g>
    ))}
    {/* Face landmarks dots */}
    {[[247,170],[207,200],[287,200],[247,248],[230,232],[264,232]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="2.5" fill={T} opacity="0.6"/>
    ))}
    {/* Status badge */}
    <rect x="162" y="360" width="170" height="26" rx="13" fill="#dcfce7"/>
    <text x="247" y="377" fontSize="11" fontWeight="700" fill="#15803d" textAnchor="middle" fontFamily={F}>Liveness Detected</text>
    {/* Right panel — results */}
    <rect x="446" y="72" width="400" height="400" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
    <text x="466" y="100" fontSize="13" fontWeight="700" fill={N} fontFamily={F}>Verification Result</text>
    {/* Verified badge */}
    <rect x="466" y="108" width="130" height="30" rx="8" fill="#dcfce7"/>
    <text x="531" y="127" fontSize="13" fontWeight="700" fill="#15803d" textAnchor="middle" fontFamily={F}>VERIFIED</text>
    {/* Match score */}
    <text x="466" y="168" fontSize="11" fill="#9ca3af" fontFamily={F}>Face Match Score</text>
    <rect x="466" y="175" width="360" height="10" rx="5" fill="#f3f4f6"/>
    <rect x="466" y="175" width="354" height="10" rx="5" fill={T} opacity="0.8"/>
    <text x="826" y="186" fontSize="13" fontWeight="700" fill={T} textAnchor="end" fontFamily={F}>98.4%</text>
    {/* Fields */}
    {[
      {label:"Liveness Check", val:"PASSED", vc:T},
      {label:"Anti-Spoofing",  val:"3D PASSED", vc:T},
      {label:"Document Type",  val:"KTP", vc:N},
      {label:"NIK",            val:"3271 0409 ••••••", vc:N},
      {label:"Nama",           val:"BUDI SANTOSO", vc:N},
      {label:"Response Time",  val:"187 ms", vc:"#6366f1"},
    ].map((r,i) => (
      <g key={i}>
        <text x="466" y={224+i*32} fontSize="10" fill="#9ca3af" fontFamily={F}>{r.label}</text>
        <text x="466" y={240+i*32} fontSize="12" fontWeight="600" fill={r.vc} fontFamily={F}>{r.val}</text>
        <line x1="466" y1={248+i*32} x2="826" y2={248+i*32} stroke="#f3f4f6" strokeWidth="1"/>
      </g>
    ))}
  </svg>
);

// ── Variant 1: Watchlist Screening / AML ──
const ScreenWatchlist: React.FC = () => {
  const rows = [
    {name:"Ahmad Yani Rifai",entity:"Person",country:"Indonesia",risk:"HIGH",status:"FLAGGED",rc:"#fef2f2",sc:"#dc2626",lists:"PEP, OFAC"},
    {name:"PT Maju Jaya",    entity:"Corporate",country:"Indonesia",risk:"MED",status:"REVIEW",rc:"#fffbeb",sc:"#d97706",lists:"Local WL"},
    {name:"Dewi Rahayu",     entity:"Person",country:"Philippines",risk:"LOW",status:"CLEAR",rc:"#f0fdf4",sc:"#16a34a",lists:"—"},
  ];
  return (
    <svg viewBox="0 0 860 500" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{width:"100%",height:"100%",display:"block"}} preserveAspectRatio="xMidYMid slice">
      <rect width="860" height="500" fill="#f4f6fb"/>
      <Chrome activeNav={2}/>
      <text x="66" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>Watchlist</text>
      <text x="118" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>/</text>
      <text x="128" y="62" fontSize="11" fontWeight="600" fill={N} fontFamily={F}>Screening Results</text>
      {/* Main card */}
      <rect x="62" y="70" width="784" height="412" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
      {/* Search bar */}
      <rect x="80" y="88" width="500" height="34" rx="8" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1"/>
      <text x="102" y="110" fontSize="12" fill="#9ca3af" fontFamily={F}>BUDI SANTOSO — screening 3 entities...</text>
      <rect x="590" y="88" width="100" height="34" rx="8" fill={T}/>
      <text x="640" y="110" fontSize="12" fontWeight="600" fill="white" textAnchor="middle" fontFamily={F}>Re-Screen</text>
      {/* Stats chips */}
      {[
        {v:"3 Checked",bg:"#f3f4f6",c:N},{v:"1 Flagged",bg:"#fef2f2",c:"#dc2626"},
        {v:"1 Review",bg:"#fffbeb",c:"#d97706"},{v:"1 Clear",bg:"#f0fdf4",c:"#16a34a"},
        {v:"312 ms",bg:"#f0fdf9",c:T},
      ].map((s,i) => (
        <g key={i}>
          <rect x={80+i*140} y="134" width="128" height="24" rx="6" fill={s.bg}/>
          <text x={144+i*140} y="150" fontSize="11" fontWeight="600" fill={s.c} textAnchor="middle" fontFamily={F}>{s.v}</text>
        </g>
      ))}
      {/* Table header */}
      <rect x="80" y="170" width="726" height="28" rx="6" fill="#f8f9fb"/>
      {["Name","Entity","Country","Lists","Risk","Status"].map((h,i) => (
        <text key={i} x={[96,256,356,436,556,656][i]} y="188" fontSize="10" fontWeight="600"
          fill="#9ca3af" fontFamily={F}>{h}</text>
      ))}
      {/* Table rows */}
      {rows.map((r,i) => (
        <g key={i}>
          <rect x="80" y={200+i*66} width="726" height="60" rx="6"
            fill={i%2===0?"#fafafa":"white"} stroke="#f3f4f6" strokeWidth="1"/>
          <text x="96"  y={218+i*66} fontSize="12" fontWeight="600" fill={N} fontFamily={F}>{r.name}</text>
          <text x="96"  y={234+i*66} fontSize="10" fill="#9ca3af" fontFamily={F}>{r.entity}</text>
          <text x="256" y={224+i*66} fontSize="11" fill={N} fontFamily={F}>{r.entity}</text>
          <text x="356" y={224+i*66} fontSize="11" fill={N} fontFamily={F}>{r.country}</text>
          <text x="436" y={224+i*66} fontSize="10" fill="#6b7280" fontFamily={F}>{r.lists}</text>
          <rect x="546" y={210+i*66} width="50" height="20" rx="5" fill={r.rc}/>
          <text x="571" y={224+i*66} fontSize="10" fontWeight="700" fill={r.sc} textAnchor="middle" fontFamily={F}>{r.risk}</text>
          <rect x="636" y={210+i*66} width="70" height="20" rx="5" fill={r.rc}/>
          <text x="671" y={224+i*66} fontSize="10" fontWeight="700" fill={r.sc} textAnchor="middle" fontFamily={F}>{r.status}</text>
        </g>
      ))}
      {/* Footer note */}
      <text x="80" y="462" fontSize="10" fill="#9ca3af" fontFamily={F}>Powered by TrustEngine Watchlist API — Global databases: PEP, OFAC, UN, EU, Local</text>
    </svg>
  );
};

// ── Variant 2: Biometric Face Match (e-wallet) ──
const ScreenFaceMatch: React.FC = () => (
  <svg viewBox="0 0 860 500" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{width:"100%",height:"100%",display:"block"}} preserveAspectRatio="xMidYMid slice">
    <rect width="860" height="500" fill="#f4f6fb"/>
    <Chrome activeNav={1}/>
    <text x="66" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>Face Engine</text>
    <text x="138" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>/</text>
    <text x="148" y="62" fontSize="11" fontWeight="600" fill={N} fontFamily={F}>1:1 Face Comparison</text>
    {/* Main card */}
    <rect x="62" y="70" width="784" height="412" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
    <text x="82" y="100" fontSize="13" fontWeight="700" fill={N} fontFamily={F}>Biometric Match — Transaction Verification</text>
    {/* Photo frame 1 */}
    <rect x="82" y="112" width="220" height="240" rx="12" fill="#f8f9fb" stroke="#e5e7eb" strokeWidth="1"/>
    <text x="192" y="136" fontSize="11" fontWeight="600" fill="#6b7280" textAnchor="middle" fontFamily={F}>KTP Photo</text>
    <ellipse cx="192" cy="220" rx="50" ry="60" fill="#e5e7eb"/>
    <circle cx="192" cy="196" r="22" fill="#d1d5db"/>
    <text x="192" y="275" fontSize="10" fill="#9ca3af" textAnchor="middle" fontFamily={F}>Source image</text>
    <rect x="82" y="346" width="220" height="26" rx="6" fill="#f3f4f6"/>
    <text x="192" y="363" fontSize="10" fill="#6b7280" textAnchor="middle" fontFamily={F}>Confidence: 99.1%</text>
    {/* Arrow */}
    <path d="M318 230 L390 230" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M380 222 L392 230 L380 238" stroke={T} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <circle cx="354" cy="230" r="22" fill="#e0f7f1"/>
    <text x="354" y="235" fontSize="20" fontWeight="700" fill={T} textAnchor="middle" fontFamily={F}>~</text>
    {/* Photo frame 2 */}
    <rect x="400" y="112" width="220" height="240" rx="12" fill="#f0fdf9" stroke={T} strokeWidth="1.5"/>
    <text x="510" y="136" fontSize="11" fontWeight="600" fill={T} textAnchor="middle" fontFamily={F}>Live Selfie</text>
    <ellipse cx="510" cy="220" rx="50" ry="60" fill="#d1fae5"/>
    <circle cx="510" cy="196" r="22" fill="#a7f3d0"/>
    <text x="510" y="275" fontSize="10" fill={T} textAnchor="middle" fontFamily={F}>Live capture</text>
    <rect x="400" y="346" width="220" height="26" rx="6" fill="#dcfce7"/>
    <text x="510" y="363" fontSize="10" fontWeight="600" fill="#15803d" textAnchor="middle" fontFamily={F}>Liveness: PASSED</text>
    {/* Score panel */}
    <rect x="642" y="112" width="184" height="260" rx="12" fill="#f0fdf9" stroke={T} strokeWidth="1"/>
    <text x="734" y="142" fontSize="11" fontWeight="600" fill="#6b7280" textAnchor="middle" fontFamily={F}>Match Score</text>
    <text x="734" y="200" fontSize="44" fontWeight="800" fill={T} textAnchor="middle" fontFamily={F}>99.8</text>
    <text x="734" y="222" fontSize="14" fontWeight="600" fill={T} textAnchor="middle" fontFamily={F}>%</text>
    <circle cx="734" cy="180" r="52" stroke={T} strokeWidth="3" fill="none" opacity="0.15"/>
    {[
      {lbl:"Anti-Spoof",val:"PASSED",c:T},
      {lbl:"3D Depth",  val:"PASSED",c:T},
      {lbl:"Threshold", val:">95%",  c:"#6b7280"},
    ].map((r,i) => (
      <g key={i}>
        <text x="650" y={250+i*28} fontSize="10" fill="#9ca3af" fontFamily={F}>{r.lbl}</text>
        <text x="820" y={250+i*28} fontSize="10" fontWeight="700" fill={r.c} textAnchor="end" fontFamily={F}>{r.val}</text>
        <line x1="650" y1={256+i*28} x2="820" y2={256+i*28} stroke="#e5e7eb" strokeWidth="1"/>
      </g>
    ))}
    {/* Decision */}
    <rect x="642" y="388" width="184" height="54" rx="10" fill={T}/>
    <text x="734" y="410" fontSize="12" fontWeight="700" fill="white" textAnchor="middle" fontFamily={F}>Decision</text>
    <text x="734" y="430" fontSize="16" fontWeight="800" fill="white" textAnchor="middle" fontFamily={F}>VERIFIED</text>
    {/* Bottom stats */}
    {[
      {v:"24,847",l:"Verifications"},
      {v:"12,000+",l:"Frauds blocked"},
      {v:"99.97%",l:"Accuracy"},
    ].map((s,i) => (
      <g key={i}>
        <text x={100+i*220} y={436} fontSize="16" fontWeight="800" fill={T} fontFamily={F}>{s.v}</text>
        <text x={100+i*220} y={454} fontSize="10" fill="#9ca3af" fontFamily={F}>{s.l} this month</text>
      </g>
    ))}
  </svg>
);

// ── Variant 3: Gov Identity / KTP OCR ──
const ScreenKTPOCR: React.FC = () => {
  const fields = [
    {lbl:"NIK",   val:"3271 0409 0487 0001",conf:99},
    {lbl:"Nama",  val:"BUDI SANTOSO",        conf:99},
    {lbl:"TTL",   val:"Jakarta, 09-04-1987", conf:97},
    {lbl:"JK",    val:"LAKI-LAKI",           conf:99},
    {lbl:"Alamat",val:"Jl. Kebon Jeruk No.5",conf:96},
    {lbl:"Kota",  val:"Jakarta Barat",       conf:98},
  ];
  return (
    <svg viewBox="0 0 860 500" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{width:"100%",height:"100%",display:"block"}} preserveAspectRatio="xMidYMid slice">
      <rect width="860" height="500" fill="#f4f6fb"/>
      <Chrome activeNav={3}/>
      <text x="66" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>Document OCR</text>
      <text x="158" y="62" fontSize="11" fill="#9ca3af" fontFamily={F}>/</text>
      <text x="168" y="62" fontSize="11" fontWeight="600" fill={N} fontFamily={F}>KTP Extraction</text>
      {/* KTP card visual */}
      <rect x="62" y="72" width="340" height="220" rx="10" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
      <rect x="62" y="72" width="340" height="44" rx="10" fill="#dc2626"/>
      <rect x="62" y="106" width="340" height="10" fill="#dc2626"/>
      <text x="232" y="90" fontSize="9" fontWeight="700" fill="white" textAnchor="middle" fontFamily={F}>REPUBLIK INDONESIA</text>
      <text x="232" y="104" fontSize="8" fontWeight="600" fill="white" textAnchor="middle" fontFamily={F}>KARTU TANDA PENDUDUK</text>
      <text x="232" y="120" fontSize="8" fill="#6b7280" textAnchor="middle" fontFamily={F}>Provinsi DKI Jakarta</text>
      {/* NIK row */}
      <text x="78" y="142" fontSize="8" fill="#9ca3af" fontFamily={F}>NIK:</text>
      <text x="106" y="142" fontSize="9" fontWeight="700" fill={N} fontFamily={F}>3271 0409 0487 0001</text>
      {/* Photo placeholder */}
      <rect x="78" y="150" width="64" height="80" rx="4" fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="1"/>
      <circle cx="110" cy="175" r="16" fill="#d1d5db"/>
      <ellipse cx="110" cy="215" rx="22" ry="12" fill="#d1d5db"/>
      {/* Fields */}
      {[
        ["Nama","BUDI SANTOSO",154],
        ["TTL","Jakarta, 09-04-1987",170],
        ["JK","LAKI-LAKI",186],
        ["Gol. Darah","O",202],
        ["Alamat","Jl. Kebon Jeruk No. 5",218],
        ["RT/RW","003/008",234],
      ].map(([lbl,val,y],i) => (
        <g key={i}>
          <text x="154" y={Number(y)} fontSize="8" fill="#9ca3af" fontFamily={F}>{lbl}</text>
          <text x="200" y={Number(y)} fontSize="8" fontWeight="500" fill={N} fontFamily={F}>: {val}</text>
        </g>
      ))}
      {/* Status */}
      <rect x="62" y="306" width="340" height="36" rx="8" fill="#dcfce7"/>
      <text x="232" y="329" fontSize="12" fontWeight="700" fill="#15803d" textAnchor="middle" fontFamily={F}>Document Verified — Confidence: 97.8%</text>
      {/* Processing stats */}
      {[{v:"0.8s",l:"Extraction time"},{v:"6",l:"Fields detected"},{v:"98%",l:"Avg confidence"}].map((s,i) => (
        <g key={i}>
          <text x={80+i*116} y={376} fontSize="14" fontWeight="700" fill={T} fontFamily={F}>{s.v}</text>
          <text x={80+i*116} y={392} fontSize="9" fill="#9ca3af" fontFamily={F}>{s.l}</text>
        </g>
      ))}
      {/* Extraction results table */}
      <rect x="416" y="72" width="430" height="400" rx="12" fill="white" stroke="#e5e7eb" strokeWidth="1"/>
      <text x="436" y="100" fontSize="13" fontWeight="700" fill={N} fontFamily={F}>Extracted Fields</text>
      <rect x="436" y="108" width="120" height="20" rx="5" fill="#dcfce7"/>
      <text x="496" y="122" fontSize="10" fontWeight="700" fill="#15803d" textAnchor="middle" fontFamily={F}>VERIFIED</text>
      {/* Table header */}
      <rect x="436" y="136" width="390" height="22" rx="5" fill="#f8f9fb"/>
      <text x="446" y="151" fontSize="9" fontWeight="600" fill="#9ca3af" fontFamily={F}>Field</text>
      <text x="546" y="151" fontSize="9" fontWeight="600" fill="#9ca3af" fontFamily={F}>Extracted Value</text>
      <text x="790" y="151" fontSize="9" fontWeight="600" fill="#9ca3af" textAnchor="end" fontFamily={F}>Conf.</text>
      {fields.map((r,i) => (
        <g key={i}>
          <rect x="436" y={162+i*36} width="390" height="32" rx="5"
            fill={i%2===0?"#fafafa":"white"}/>
          <text x="446" y={182+i*36} fontSize="10" fontWeight="600" fill="#6b7280" fontFamily={F}>{r.lbl}</text>
          <text x="546" y={182+i*36} fontSize="11" fontWeight="500" fill={N} fontFamily={F}>{r.val}</text>
          {/* Confidence bar */}
          <rect x="750" y={169+i*36} width="68" height="10" rx="5" fill="#f3f4f6"/>
          <rect x="750" y={169+i*36} width={68*(r.conf/100)} height="10" rx="5" fill={T} opacity="0.7"/>
          <text x="790" y={182+i*36} fontSize="9" fontWeight="700" fill={T} textAnchor="end" fontFamily={F}>{r.conf}%</text>
        </g>
      ))}
      <text x="436" y="406" fontSize="10" fill="#9ca3af" fontFamily={F}>TrustEngine OCR API — Processed in 0.8s</text>
      <rect x="436" y="416" width="390" height="32" rx="8" fill="#f0fdf9" stroke={T} strokeWidth="1"/>
      <text x="631" y="436" fontSize="11" fontWeight="600" fill={T} textAnchor="middle" fontFamily={F}>All fields extracted successfully</text>
    </svg>
  );
};

// Map variant index to screen component
const SCREENS = [ScreenEKYC, ScreenWatchlist, ScreenFaceMatch, ScreenKTPOCR];

// ── Inline tech icons ──────────────────────────────────────────────────────

// ── Inline tech icons ──────────────────────────────────────────────────────

const IconPython = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#f0f4ff"/>
    <path d="M24 8c-5.8 0-9.5 2.6-9.5 6v3h9.5v1H12c-3.5 0-6.5 2.1-6.5 9.5 0 6.5 2.5 9.5 6.5 9.5h3v-4.5c0-4 2.5-6 6.5-6h6c3.5 0 6-2.6 6-6V14c0-3.5-2.5-6-9.5-6z" fill="#3776AB"/>
    <path d="M24 40c5.8 0 9.5-2.6 9.5-6v-3H24v-1h12c3.5 0 6.5-2.1 6.5-9.5 0-6.5-2.5-9.5-6.5-9.5h-3v4.5c0 4-2.5 6-6.5 6h-6c-3.5 0-6 2.6-6 6v8c0 3.5 2.5 4.5 9.5 4.5z" fill="#FFD43B"/>
    <circle cx="21" cy="13" r="1.5" fill="#fff"/>
    <circle cx="27" cy="35" r="1.5" fill="#fff"/>
  </svg>
);

const IconOpenCV = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#fff8f0"/>
    <circle cx="16" cy="20" r="8" fill="#5C8EFF" opacity="0.9"/>
    <circle cx="32" cy="20" r="8" fill="#FF4444" opacity="0.9"/>
    <circle cx="24" cy="32" r="8" fill="#00C896" opacity="0.9"/>
    <circle cx="24" cy="24" r="4" fill="#fff" opacity="0.7"/>
  </svg>
);


const IconNLP = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#f0fff8"/>
    <rect x="10" y="14" width="28" height="4" rx="2" fill="#00C896" opacity="0.3"/>
    <rect x="10" y="22" width="22" height="4" rx="2" fill="#00C896" opacity="0.5"/>
    <rect x="10" y="30" width="18" height="4" rx="2" fill="#00C896" opacity="0.8"/>
    <circle cx="36" cy="32" r="7" fill="#00C896"/>
    <path d="M33 32l2 2 4-4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconML = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#f5f0ff"/>
    <circle cx="24" cy="24" r="4" fill="#8B5CF6"/>
    <circle cx="12" cy="16" r="3" fill="#8B5CF6" opacity="0.6"/>
    <circle cx="36" cy="16" r="3" fill="#8B5CF6" opacity="0.6"/>
    <circle cx="12" cy="32" r="3" fill="#8B5CF6" opacity="0.6"/>
    <circle cx="36" cy="32" r="3" fill="#8B5CF6" opacity="0.6"/>
    <line x1="24" y1="20" x2="12" y2="19" stroke="#8B5CF6" strokeWidth="1.5" opacity="0.5"/>
    <line x1="24" y1="20" x2="36" y2="19" stroke="#8B5CF6" strokeWidth="1.5" opacity="0.5"/>
    <line x1="24" y1="28" x2="12" y2="29" stroke="#8B5CF6" strokeWidth="1.5" opacity="0.5"/>
    <line x1="24" y1="28" x2="36" y2="29" stroke="#8B5CF6" strokeWidth="1.5" opacity="0.5"/>
  </svg>
);

const IconOCR = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#f0f8ff"/>
    <rect x="10" y="10" width="28" height="28" rx="3" stroke="#3B82F6" strokeWidth="1.8" fill="none"/>
    <rect x="14" y="17" width="12" height="2.5" rx="1" fill="#3B82F6" opacity="0.5"/>
    <rect x="14" y="22" width="20" height="2.5" rx="1" fill="#3B82F6" opacity="0.5"/>
    <rect x="14" y="27" width="16" height="2.5" rx="1" fill="#3B82F6" opacity="0.5"/>
    <rect x="9" y="9" width="7" height="2" rx="1" fill="#3B82F6"/>
    <rect x="9" y="9" width="2" height="7" rx="1" fill="#3B82F6"/>
    <rect x="32" y="9" width="7" height="2" rx="1" fill="#3B82F6"/>
    <rect x="37" y="9" width="2" height="7" rx="1" fill="#3B82F6"/>
    <rect x="9" y="37" width="7" height="2" rx="1" fill="#3B82F6"/>
    <rect x="9" y="32" width="2" height="7" rx="1" fill="#3B82F6"/>
    <rect x="32" y="37" width="7" height="2" rx="1" fill="#3B82F6"/>
    <rect x="37" y="32" width="2" height="7" rx="1" fill="#3B82F6"/>
  </svg>
);

const IconRestAPI = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#f0fdf4"/>
    <path d="M10 24h6M32 24h6" stroke="#00C896" strokeWidth="2" strokeLinecap="round"/>
    <rect x="16" y="18" width="16" height="12" rx="3" fill="none" stroke="#00C896" strokeWidth="1.8"/>
    <path d="M21 24h6M24 21v6" stroke="#00C896" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="10" cy="24" r="2.5" fill="#00C896" opacity="0.5"/>
    <circle cx="38" cy="24" r="2.5" fill="#00C896" opacity="0.5"/>
  </svg>
);

const IconAML = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#fff0f0"/>
    <path d="M24 10L13 15v8c0 7.2 4.7 13.9 11 15.9 6.3-2 11-8.7 11-15.9v-8L24 10z"
      fill="rgba(239,68,68,0.1)" stroke="#EF4444" strokeWidth="1.8" strokeLinejoin="round"/>
    <circle cx="24" cy="26" r="5" stroke="#EF4444" strokeWidth="1.5" fill="none"/>
    <circle cx="24" cy="19" r="2" fill="#EF4444" opacity="0.4"/>
    <line x1="31" y1="14" x2="35" y2="10" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="36" cy="9" r="2" fill="#EF4444"/>
  </svg>
);

const IconBiometric = () => (
  <svg viewBox="0 0 48 48" width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#f0fdf9"/>
    <path d="M18 14c-2 1.5-4 4-4 8 0 5 2 8 4 10" stroke="#00C896" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
    <path d="M30 14c2 1.5 4 4 4 8 0 5-2 8-4 10" stroke="#00C896" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
    <path d="M21 19c-1 1-1.5 2.5-1.5 5 0 2.5.5 4.5 1.5 6" stroke="#00C896" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <path d="M27 19c1 1 1.5 2.5 1.5 5 0 2.5-.5 4.5-1.5 6" stroke="#00C896" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="24" y1="17" x2="24" y2="33" stroke="#00C896" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ProjectSection: React.FC = () => {
  useEffect(() => {
    // ✅ Set dynamic background image
    const bgElement = document.querySelector<HTMLElement>(".project.bg_img");
    if (bgElement) {
      bgElement.style.backgroundImage = `url(${projectBg})`;
    }

    // ✅ Scroll activation logic
    const items = document.querySelectorAll<HTMLElement>(".xb-project-item");
    const paginations = document.querySelectorAll<HTMLElement>(".xb-project-pagination li");

    if (!items.length || !paginations.length) return;

    items.forEach((item) => {
      item.style.transition = "opacity 0.6s ease";
      item.style.opacity = "1";
    });

    const updateActive = () => {
      let indexToActivate = 0;
      const triggerLine = window.innerHeight * 0.3;

      items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        if (rect.top <= triggerLine) {
          indexToActivate = index;
        }
      });

      paginations.forEach((el) => el.classList.remove("active"));
      if (paginations[indexToActivate]) {
        paginations[indexToActivate].classList.add("active");
      }

      items.forEach((item, i) => {
        if (i === indexToActivate) {
          item.style.opacity = "1";
        } else if (i < indexToActivate) {
          item.style.opacity = "0.3";
        } else {
          item.style.opacity = "1";
        }
      });
    };

    window.addEventListener("scroll", updateActive);
    updateActive();

    return () => {
      window.removeEventListener("scroll", updateActive);
    };
  }, []);

  return (
    <section className="project bg_img pt-135 pb-150">
      <div className="container">
        <div className="sec-title custom-sec-title xb-sec-padding text-center">
          <span className="sub-title">Case Studies</span>
          <h2 className="title">
            Real results from real enterprise clients
          </h2>
          <div className="xb-heading-btn d-inline">
            <Link className="thm-btn agency-btn" to="/project">
              <span className="text">view all case studies</span>
              <span className="arrow">
                <span className="arrow-icon">
                  {/* Double SVG arrow */}
                  {[...Array(2)].map((_, i) => (
                    <svg
                      key={i}
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="5.06592"
                        y="19.9785"
                        width="20.5712"
                        height="2.61221"
                        transform="rotate(-40.2798 5.06592 19.9785)"
                        fill="white"
                      />
                      <rect
                        x="7.97095"
                        y="7.24463"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 7.97095 7.24463)"
                        fill="white"
                      />
                      <rect
                        x="11.6523"
                        y="7.54834"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 11.6523 7.54834)"
                        fill="white"
                      />
                      <rect
                        x="15.334"
                        y="7.85205"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 15.334 7.85205)"
                        fill="white"
                      />
                      <rect
                        x="18.7119"
                        y="11.8374"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.7119 11.8374)"
                        fill="white"
                      />
                      <rect
                        x="18.4084"
                        y="15.52"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.4084 15.52)"
                        fill="white"
                      />
                      <rect
                        x="18.104"
                        y="19.2012"
                        width="2.61221"
                        height="2.61221"
                        transform="rotate(-40.2798 18.104 19.2012)"
                        fill="white"
                      />
                    </svg>
                  ))}
                </span>
              </span>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mxw-1800">
        <div className="xb-project-wrap">
          {/* Pagination */}
          <div className="xb-project-pagination-wrap">
            <ul className="xb-project-pagination">
              <li>1</li>
              <li className="active">2</li>
              <li>3</li>
              <li>4</li>
            </ul>
          </div>

          {/* Project items */}
          <div className="xb-project-inner">
            {[
              {
                title: "Digital onboarding at scale..",
                desc: "A leading fintech lender cut their KYC onboarding time from 3 days to under 90 seconds using TrustEngine's eKYC API with passive liveness and document OCR — reducing drop-off rates by 42%.",
                industry: "Fintech & Lending",
                country: "Indonesia",
                techs: [<IconPython />, <IconOpenCV />, <IconOCR />],
              },
              {
                title: "AML screening for neobank..",
                desc: "A regional neobank integrated TrustEngine's Database Screening API to run real-time AML, PEP, and sanctions checks on every account opening — meeting OJK compliance with zero engineering overhead.",
                industry: "Banking & Insurance",
                country: "Southeast Asia",
                techs: [<IconPython />, <IconAML />, <IconRestAPI />],
              },
              {
                title: "Face match for e-wallet..",
                desc: "An e-wallet provider deployed TrustEngine's 1:1 face recognition with 3D anti-spoofing to secure high-value transactions — achieving 99.97% accuracy and blocking 12,000+ fraud attempts monthly.",
                industry: "E-commerce & Payments",
                country: "Philippines",
                techs: [<IconPython />, <IconBiometric />, <IconML />],
              },
              {
                title: "Gov-grade identity platform..",
                desc: "A public sector agency deployed TrustEngine for civil servant identity verification, integrating OCR-based KTP extraction with biometric matching — processing 2M+ verifications per month with 99.9% uptime.",
                industry: "Government & Public Services",
                country: "Indonesia",
                techs: [<IconPython />, <IconOCR />, <IconNLP />],
              },
            ].map((project, index) => (
              <div
                key={index}
                className="xb-project-item"
              >
                {/* Product screen — right-side panel */}
                <div className="project-screen-panel" style={{
                  position: "absolute",
                  top: 24, right: 24, bottom: 24,
                  left: "42%",
                  overflow: "hidden",
                  borderRadius: 12,
                  boxShadow: "0 8px 40px rgba(15,32,64,0.18), 0 0 0 1px rgba(15,32,64,0.08)",
                  pointerEvents: "none",
                  zIndex: 0,
                }}>
                  {React.createElement(SCREENS[index % SCREENS.length])}
                </div>
                <div className="xb-project-content">
                  <div className="xb-item--inner xb-border">
                    <h2 className="xb-item--title">{project.title}</h2>
                    <p className="xb-item--content">{project.desc}</p>
                    <ul className="xb-item--list ul_li">
                      <li>
                        Industry: <span>{project.industry}</span>
                      </li>
                      <li>
                        Country: <span>{project.country}</span>
                      </li>
                    </ul>
                    <div className="xb-item--technologie ul_li">
                      <span>Core Technologies:</span>
                      <ul className="list-unstyled ul_li">
                        {project.techs.map((icon, i) => (
                          <li key={i}>{icon}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="xb-item---btn mt-70">
                      <Link className="thm-btn agency-btn" to="/project-details">
                        <span className="text">read more</span>
                        <span className="arrow">
                          <span className="arrow-icon">
                            {[...Array(2)].map((_, i) => (
                              <svg
                                key={i}
                                width="28"
                                height="28"
                                viewBox="0 0 28 28"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="5.06592"
                                  y="19.9785"
                                  width="20.5712"
                                  height="2.61221"
                                  transform="rotate(-40.2798 5.06592 19.9785)"
                                  fill="white"
                                />
                                <rect
                                  x="7.97095"
                                  y="7.24463"
                                  width="2.61221"
                                  height="2.61221"
                                  transform="rotate(-40.2798 7.97095 7.24463)"
                                  fill="white"
                                />
                                <rect
                                  x="11.6523"
                                  y="7.54834"
                                  width="2.61221"
                                  height="2.61221"
                                  transform="rotate(-40.2798 11.6523 7.54834)"
                                  fill="white"
                                />
                                <rect
                                  x="15.334"
                                  y="7.85205"
                                  width="2.61221"
                                  height="2.61221"
                                  transform="rotate(-40.2798 15.334 7.85205)"
                                  fill="white"
                                />
                                <rect
                                  x="18.7119"
                                  y="11.8374"
                                  width="2.61221"
                                  height="2.61221"
                                  transform="rotate(-40.2798 18.7119 11.8374)"
                                  fill="white"
                                />
                                <rect
                                  x="18.4084"
                                  y="15.52"
                                  width="2.61221"
                                  height="2.61221"
                                  transform="rotate(-40.2798 18.4084 15.52)"
                                  fill="white"
                                />
                                <rect
                                  x="18.104"
                                  y="19.2012"
                                  width="2.61221"
                                  height="2.61221"
                                  transform="rotate(-40.2798 18.104 19.2012)"
                                  fill="white"
                                />
                              </svg>
                            ))}
                          </span>
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectSection;
